// src/pages/MockInterview.jsx
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { submitMockAnswer, endMockInterview ,getMockReport} from "../api/mockClient";

function formatTime(sec) {
  const m = Math.floor(sec / 60).toString().padStart(2, "0");
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function MockInterview() {
  const { interviewId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const initialQuestions = location.state?.questions || [];
  const [questions, setQuestions] = useState(initialQuestions);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({}); // { questionId: { text, submitted } }
  const [perRemaining, setPerRemaining] = useState(60);
  const perQuestionTime = 60;
  const [totalRemaining, setTotalRemaining] = useState(
    (questions.length || 20) * perQuestionTime
  );
  const totalTimerRef = useRef(null);
  const perTimerRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const [ttsSpeaking, setTtsSpeaking] = useState(false); // 🔥 now actively used
  const [toast, setToast] = useState(null);

  // If interview has no questions, redirect back
  useEffect(() => {
    if (!questions || questions.length === 0) {
      setTimeout(() => {
        alert("No interview data. Please start interview from the Prep page.");
        navigate("/prep");
      }, 200);
      return;
    }

    // init timers
    setPerRemaining(perQuestionTime);
    setTotalRemaining(questions.length * perQuestionTime);

    totalTimerRef.current = setInterval(() => {
      setTotalRemaining((t) => {
        if (t <= 1) {
          clearInterval(totalTimerRef.current);
          handleEnd();
          return 0;
        }
        return t - 1;
      });
    }, 1000);

    perTimerRef.current = setInterval(() => {
      setPerRemaining((p) => {
        if (p <= 1) {
          handleAutoSubmit();
          return perQuestionTime;
        }
        return p - 1;
      });
    }, 1000);

    startCamera();

    return () => {
      clearInterval(totalTimerRef.current);
      clearInterval(perTimerRef.current);
      stopCamera();
      stopRecognition();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // reset per-question timer when index changes
  useEffect(() => {
    setPerRemaining(perQuestionTime);
  }, [currentIndex]);

  const startCamera = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      });
      streamRef.current = s;
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        videoRef.current.play().catch(() => {});
      }
    } catch (err) {
      console.warn("Camera not available", err);
    }
  };

  const stopCamera = () => {
    try {
      streamRef.current?.getTracks()?.forEach((t) => t.stop());
      streamRef.current = null;
    } catch (e) {}
  };

  const startRecognition = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Speech recognition not supported in this browser (Chrome recommended).");
      return;
    }
    const rec = new SpeechRecognition();
    rec.lang = "en-US";
    rec.interimResults = false;
    rec.maxAlternatives = 1;

    rec.onresult = (e) => {
      const text = e.results[0][0].transcript;
      setAnswers((prev) => {
        const q = questions[currentIndex];
        const prevText = prev[q.id]?.text || "";
        return {
          ...prev,
          [q.id]: {
            ...(prev[q.id] || {}),
            text: prevText ? prevText + " " + text : text,
          },
        };
      });
    };
    rec.onerror = (e) => console.error("STT error", e);
    rec.onend = () => setListening(false);

    recognitionRef.current = rec;
    rec.start();
    setListening(true);
  };

  const stopRecognition = () => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {}
      recognitionRef.current = null;
      setListening(false);
    }
  };

  const speakQuestion = (text) => {
    if (!window.speechSynthesis) {
      alert("Text-to-speech not supported in this browser.");
      return;
    }
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.lang = "en-US";
      u.onend = () => setTtsSpeaking(false); // ✅ used now
      window.speechSynthesis.speak(u);
      setTtsSpeaking(true);
    } catch (e) {
      console.error("TTS error", e);
      setTtsSpeaking(false);
    }
  };

  const handleAnswerChange = (qid, text) => {
    setAnswers((prev) => ({
      ...prev,
      [qid]: { ...(prev[qid] || {}), text },
    }));
  };

  const handleSubmitCurrent = async (moveNext = true) => {
    if (!questions[currentIndex]) return;
    const q = questions[currentIndex];
    const payload = {
      mockInterviewId: Number(interviewId),
      questionId: q.id,
      answerText: (answers[q.id]?.text || "").trim(),
      timeTakenSec: perQuestionTime - perRemaining,
      status: (answers[q.id]?.text || "").trim() ? "answered" : "skipped",
    };
    try {
      await submitMockAnswer(payload);
      setAnswers((prev) => ({
        ...prev,
        [q.id]: { ...(prev[q.id] || {}), submitted: true },
      }));
      showToast("Saved");
    } catch (err) {
      console.error("Submit error", err);
      showToast("Save failed");
    }
    if (moveNext) {
      if (currentIndex < questions.length - 1) setCurrentIndex((i) => i + 1);
      else handleEnd();
    } else {
      setPerRemaining(perQuestionTime);
    }
  };


const handleSkip = async () => {
  if (!questions[currentIndex]) return;
  const q = questions[currentIndex];
  const payload = {
    mockInterviewId: Number(interviewId),
    questionId: q.id,
    answerText: null,
    timeTakenSec: perQuestionTime - perRemaining,
    status: "skipped", // mark as skipped
  };
  try {
    await submitMockAnswer(payload);
    setAnswers((prev) => ({
      ...prev,
      [q.id]: { submitted: true, skipped: true },
    }));
    showToast("Skipped");
  } catch (err) {
    console.error("Skip error", err);
    showToast("Skip failed");
  }
  if (currentIndex < questions.length - 1) setCurrentIndex((i) => i + 1);
  else handleEnd();
};




  const handleAutoSubmit = async () => {
    await handleSubmitCurrent(true);
  };

  const handlePrev = () => {
    if (currentIndex > 0) setCurrentIndex((i) => i - 1);
  };

  const handleNext = () => {
    handleSubmitCurrent(true);
  };

  const handleEnd = async () => {
    clearInterval(totalTimerRef.current);
    clearInterval(perTimerRef.current);
    stopCamera();
    stopRecognition();
   try {
  // End interview (POST request)
  await endMockInterview(interviewId); // don't expect report here

  // Fetch the report separately (GET request)
  const response = await getMockReport(interviewId); // GET /mock/report/:interviewId
  const reportData = response.data.report; // <- use response.data

  showToast("Interview ended");

  // Navigate to report page with state
  navigate(`/mock/${interviewId}/report`, { state: { report: reportData } });
} catch (err) {
  console.error("End error", err);
  alert("Error ending interview");
}
};
  const showToast = (text) => {
    setToast(text);
    setTimeout(() => setToast(null), 2500);
  };

  if (!questions || questions.length === 0) {
  return (
    <div className="p-6 text-center">
      <p className="text-gray-600">No questions loaded.</p>
      <button
        onClick={() => navigate("/prep")}
        className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded"
      >
        Go to Prep Page
      </button>
    </div>
  );
}
  const q = questions[currentIndex];

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow p-6 relative">
        {/* Top bar */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold">Mock Interview</h2>
            <div className="text-sm text-gray-500">
              Question {currentIndex + 1} of {questions.length}
            </div>
          </div>

          <div className="text-right">
            <div className="text-sm text-gray-500">Total</div>
            <div className="font-mono font-semibold">
              {formatTime(totalRemaining)}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <div className="p-4 border rounded-lg bg-gray-50 min-h-[120px]">
              <div className="text-gray-700 mb-3">
                <strong>Q:</strong> {q.question}
              </div>

              <div className="flex items-center gap-3 mb-3">
                <div className="text-sm text-gray-500">
                  Time left for this question:
                </div>
                <div className="font-mono font-semibold">
                  {formatTime(perRemaining)}
                </div>

                <button
                  onClick={() => speakQuestion(q.question)}
                  className="ml-4 px-2 py-1 bg-indigo-600 text-white rounded"
                  disabled={ttsSpeaking} // 🔥 prevent multiple clicks while speaking
                >
                  {ttsSpeaking ? "🔊 Speaking..." : "🔊 Listen"}
                </button>

                <button
                  onClick={() => {
                    if (listening) stopRecognition();
                    else startRecognition();
                  }}
                  className={`ml-2 px-2 py-1 rounded ${
                    listening ? "bg-red-500 text-white" : "bg-gray-200"
                  }`}
                >
                  {listening ? "Recording..." : "🎙️ Speak Answer"}
                </button>
              </div>

              <textarea
                rows={6}
                value={answers[q.id]?.text || ""}
                onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                className="w-full border rounded p-3 mb-3"
                placeholder="Type or speak your answer here..."
              />

              <div className="flex gap-3">
                <button
                  onClick={() => handleSubmitCurrent(false)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded"
                >
                  Save
                </button>
                <button
                  onClick={handleNext}
                  className="px-4 py-2 bg-green-600 text-white rounded"
                >
                  Save & Next
                </button>
 <button
    onClick={handleSkip}
    className="px-4 py-2 bg-yellow-500 text-white rounded"
  >
    Skip
  </button>

                <button
                  onClick={handlePrev}
                  disabled={currentIndex === 0}
                  className="px-4 py-2 border rounded"
                >
                  Prev
                </button>
                <button
                  onClick={handleEnd}
                  className="ml-auto px-4 py-2 bg-red-600 text-white rounded"
                >
                  End Interview
                </button>
              </div>
            </div>
          </div>

          <aside className="p-3 border rounded-lg">
            <div className="mb-3">
              <div className="text-sm text-gray-500">Progress</div>
              <div className="w-full bg-gray-200 h-3 rounded mt-2 overflow-hidden">
                <div
                  className="h-3 bg-indigo-600"
                  style={{
                    width: `${((currentIndex + 1) / questions.length) * 100}%`,
                  }}
                />
              </div>
            </div>

            <div className="mb-3">
              <div className="text-sm text-gray-500">Webcam</div>
              <div className="mt-2">
                <video
                  ref={videoRef}
                  playsInline
                  autoPlay
                  muted
                  className="w-full rounded"
                  style={{ height: 120, objectFit: "cover" }}
                />
                <div className="text-xs text-gray-500 mt-1">
                  Camera preview — allow access if prompted
                </div>
                <button
                  onClick={stopCamera}
                  className="mt-2 px-3 py-1 border rounded text-sm"
                >
                  Stop Camera
                </button>
              </div>
            </div>

            <div>
              <div className="text-sm text-gray-500 mb-2">Answered</div>
              <ul className="text-sm space-y-1 max-h-40 overflow-auto">
                {questions.map((qq, idx) => (
                  <li
                    key={qq.id}
                    className={`flex justify-between ${
                      idx === currentIndex ? "font-semibold" : ""
                    }`}
                  >
                    <span>Q{idx + 1}</span>
                    <span>
                      {answers[qq.id]?.submitted
                        ? "✓"
                        : answers[qq.id]?.text
                        ? "●"
                        : "—"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>

        {/* small toast */}
        {toast && (
          <div className="fixed right-6 bottom-6 bg-black text-white px-4 py-2 rounded shadow">
            {toast}
          </div>
        )}
      </div>
    </div>
  );
}